import { Upsell } from "@/@types/Upsell.ts";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { bodyScript, externalScript } from "@/lib/script-options.ts";
import { useTranslation } from "react-i18next";
import { ScriptComponent } from "./ScriptComponent.tsx";

interface IntegrationScriptSheetProps {
  upsell: Upsell | null;
  isOpen: boolean;
  onClose: () => void;
}

export default function SideQuickScript({
  upsell,
  isOpen,
  onClose,
}: IntegrationScriptSheetProps) {
  const { t } = useTranslation("upsells");

  return (
    <Sheet open={isOpen} onOpenChange={onClose}>
      <SheetContent className="md:w-[504px] w-full p-0 overflow-y-auto">
        <div className="p-6 border-b">
          <SheetTitle className="text-lg font-medium">
            {t("script_integration")}
          </SheetTitle>
          <p className="text-sm text-zinc-400">
            {t("script_integration_desc")}
          </p>
        </div>

        <div className="w-full p-6 space-y-6">
          <div className="flex gap-4 relative">
            <div className="flex flex-col items-center">
              <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-[#18181B] flex items-center justify-center text-sm font-medium text-zinc-100">
                1
              </div>
              <div className="w-[1px] h-full border-l border-dashed border-zinc-800 mt-2 absolute top-8"></div>
            </div>
            <ScriptComponent
              title={
                <>
                  {t("paste_code_inside")}{" "}
                  <code className="text-primary">&lt;head&gt;</code>
                </>
              }
              script={externalScript()}
            />
          </div>

          <div className="flex gap-4 relative">
            <div className="flex flex-col items-center">
              <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-[#18181B] flex items-center justify-center text-sm font-medium text-zinc-100">
                2
              </div>
              <div className="w-[1px] h-full border-l border-dashed border-zinc-800 mt-2 absolute top-8"></div>
            </div>
            <ScriptComponent
              title={
                <>
                  {t("paste_code_inside")}{" "}
                  <code className="text-primary">&lt;body&gt;</code>
                </>
              }
              script={bodyScript(upsell)}
            />
          </div>

          <div className="flex gap-4 relative">
            <div className="flex flex-col items-center">
              <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-[#18181B] flex items-center justify-center text-sm font-medium text-zinc-100">
                3
              </div>
              <div className="w-[1px] h-full border-l border-dashed border-zinc-800 mt-2 absolute top-8"></div>
            </div>
            <div className="flex-1 pt-1">
              <span className="text-sm text-zinc-100">
                {t("save_and_publish")}
              </span>
            </div>
          </div>

          <div className="flex gap-4">
            <div className="flex flex-col items-center">
              <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-[#18181B] flex items-center justify-center text-sm font-medium text-zinc-100">
                4
              </div>
            </div>
            <div className="flex-1 pt-1">
              <span className="text-sm text-zinc-100">
                {t("test_integration")}
              </span>
            </div>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
